import { ref } from "vue";
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";
import ldClient from "@/utils/launchDarkly";
import { stripeFunctionHealthPrimaryForNewActions } from "@/utils/launchDarkly/ldFeatureKeys";
import { isUS } from "@/globals";

// Load stripe globally so it doesn't infinitely add new iframes
const stripe = ref<Stripe | null>(null);
const isLoading = ref(false);
let lastLoadedPublishableKey: string | null = null;

function getResolvedStripePublishableKey(): string {
  const ezraKey = import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY;
  const fhKey = import.meta.env.VITE_APP_FUNCTION_HEALTH_STRIPE_PUBLISHABLE_KEY;
  const fhPrimary = ldClient.variation(
    stripeFunctionHealthPrimaryForNewActions,
    isUS
  ) as boolean;
  return fhPrimary ? fhKey : ezraKey;
}

ldClient.on(`change:${stripeFunctionHealthPrimaryForNewActions}`, () => {
  stripe.value = null;
  lastLoadedPublishableKey = null;
});

export const useStripe = () => {
  async function load(waitIfAlreadyLoading = true) {
    const publishableKey = getResolvedStripePublishableKey();

    if (stripe.value && lastLoadedPublishableKey === publishableKey) {
      return stripe.value;
    }

    if (stripe.value && lastLoadedPublishableKey !== publishableKey) {
      stripe.value = null;
    }

    if (!stripe.value) {
      if (!isLoading.value) {
        isLoading.value = true;
        try {
          // Turn off advancedFraudSignals to prevent stripe phoning home on non-payment pages (this is less ideal, but erring on the side of caution for privacy concerns)
          loadStripe.setLoadParameters({ advancedFraudSignals: false });

          // Save and remove hash/query params before Stripe init to prevent capturing sensitive data (e.g., credentials)
          const originalUrl = window.location.href;
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState(null, "", cleanUrl);

          stripe.value = await loadStripe(publishableKey);
          lastLoadedPublishableKey = publishableKey;

          // Restore original URL with hash/query params
          if (originalUrl !== cleanUrl) {
            window.history.replaceState(null, "", originalUrl);
          }
        } catch (error) {
          // Stripe.js may be blocked by consent managers (e.g., Termly) or ad blockers.
          // Payment features will be unavailable, but the app won't crash.
          console.error(error);
        } finally {
          isLoading.value = false;
        }
      } else if (waitIfAlreadyLoading) {
        await waitForLoad();
        return stripe.value;
      }
    }

    return stripe.value;
  }

  function waitForLoad() {
    return new Promise<void>(resolve => {
      const intervalId = setInterval(() => {
        if (!isLoading.value) {
          clearInterval(intervalId);
          resolve();
        }
      }, 50);
    });
  }

  return { stripe, load };
};
